import { Component, ElementRef, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { FolderDto, GalleryDto } from '@picflow/types';

import { NgFor, NgIf } from '@angular/common';
import { fuzzySearch } from 'src/app/lib/fuzzy-search';
import { sleep } from 'src/app/lib/sleep';
import { StateService } from 'src/app/services/state.service';
import { SortByPipe } from 'src/app/shared/pipes/sort-by.pipe';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { GalleryTeaserUrlPipe } from '../../../shared/pipes/gallery-teaser-url.pipe';

type GalleryTree = { gallery: GalleryDto; children: GalleryDto[]; active: boolean; disabled: boolean }[];

@Component({
  selector: 'app-finder-content',
  templateUrl: './finder-content.component.html',
  styleUrls: ['./finder-content.component.scss'],
  standalone: true,
  imports: [
    NgFor,
    SvgComponent,
    TooltipDirective,
    NgIf,
    ButtonDirective,
    LoadingIndicatorComponent,
    GalleryTeaserUrlPipe,
  ],
  providers: [SortByPipe],
})
export class FinderContentComponent implements OnChanges {
  @Input() allGalleries: GalleryDto[];
  @Input() allFolders: FolderDto[];
  @Input() currentFolder: FolderDto;
  @Input() currentGallery: GalleryDto;
  @Input() searchMode: boolean;
  @Input() searchTerm: string;
  @Input() filterOut: (FolderDto | GalleryDto)[] = [];
  @Input() disableList: (FolderDto | GalleryDto)[] = [];
  @Input() hideSubGalleries = false;
  @Input() blockGallerySelection = false;
  @Input() cta?: string;
  @Input() simpleMode: boolean = false;
  @Input() loading?: string;
  @Output() onSelectFolder = new EventEmitter<FolderDto>();
  @Output() onSelectGallery = new EventEmitter<GalleryDto>();
  @Output() onResultsUpdated = new EventEmitter<(FolderDto | GalleryDto)[]>();
  galleryTree: GalleryTree = [];
  folders: FolderDto[] = [];
  autoScrolled = false;

  folderGalleryCount: { [folderId: string]: number } = {};

  constructor(private state: StateService, private sortBy: SortByPipe, private el: ElementRef<HTMLElement>) {}

  async ngOnChanges() {
    if (!this.allGalleries || !this.allFolders) return;
    this.autoScroll();
    this.calcView();
    this.calcActiveGalleries();
    this.allFolders.forEach(folder => {
      const folderGalleries = this.allGalleries.filter(g => g.folder === folder.id);
      this.folderGalleryCount[folder.id] = folderGalleries.length;
    });
  }

  private async autoScroll() {
    if (!this.currentGallery) return;
    if (this.autoScrolled) return;
    await sleep(0);
    const el = this.el.nativeElement.querySelector('#' + this.currentGallery.id);
    if (el) el.scrollIntoView();
    this.autoScrolled = true;
  }

  selectFolder(folder: FolderDto, event?: Event) {
    if (event) event.stopPropagation();
    this.onSelectFolder.emit(folder);
  }

  selectGallery(gallery: GalleryDto, event?: Event) {
    if (event) event.stopPropagation();
    if (this.blockGallerySelection) return;
    this.onSelectGallery.emit(gallery);
    return false;
  }

  calcView() {
    const { galleries, folders } = this.getGalleriesAndFolders();
    if (!this.currentFolder) {
      this.folders = folders.filter(f => f.parent_folder === null);
      this.galleryTree = this.constructGalleryTree(galleries.filter(g => g.folder === null || g.section));
    } else if (this.searchMode) {
      this.folders = folders;
      this.galleryTree = this.constructGalleryTree(galleries.filter(g => g.folder === null || g.section));
    } else {
      this.folders = folders.filter(f => f.parent_folder === this.currentFolder.id);
      this.galleryTree = this.constructGalleryTree(
        galleries.filter(g => g.folder === this.currentFolder.id || g.section)
      );
    }
    this.calcActiveGalleries();
    this.updateResults();
  }

  async updateResults() {
    await sleep(0);
    this.onResultsUpdated.emit([...this.folders, ...this.galleryTree.map(g => g.gallery)]);
  }

  private getGalleriesAndFolders() {
    const galleries = this.allGalleries.filter(g => !this.filterOut.find(o => o?.id === g.id));
    const folders = this.allFolders.filter(f => !this.filterOut.find(o => o?.id === f.id));
    if (!this.searchMode) {
      return {
        galleries,
        folders,
      };
    }
    return {
      galleries: fuzzySearch(galleries, ['title', 'description'], this.searchTerm),
      folders: fuzzySearch(folders, ['title'], this.searchTerm),
    };
  }

  constructGalleryTree(galleries: GalleryDto[]): GalleryTree {
    return this.sortBy
      .transform(galleries, this.state.sorting)
      .filter(g => !g.section)
      .map(gallery => {
        const disabled = this.disableList.some(g => gallery.id === g.id);
        if (this.hideSubGalleries) return { gallery, children: [], active: false, disabled };
        const children = galleries.filter(g => g['parent_gallery_id'] === gallery.id);
        return { gallery, children, active: false, disabled };
      });
  }

  calcActiveGalleries() {
    this.galleryTree.forEach(item => {
      item.active = false;
      if (!this.currentGallery) return;
      if (item.gallery.id === this.currentGallery.id) item.active = true;
      if (item.children.some(c => c.id === this.currentGallery.id)) item.active = true;
    });
  }
}
